/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Online Users Modal Component
 * Modal for viewing currently online users with geo-location data
 */

import { X, User, Shield, Globe, Monitor, Wifi, WifiOff, MapPin } from 'lucide-react'
import { useOnlineUsers } from '../../hooks/useOnlineUsers'
import { getCountryFlag, getGeoDisplayInfo } from '../../utils/geoUtils'

const OnlineUsersModal = ({ isOpen, onClose }) => {
  const { onlineCount, users, isLoading, isConnected, refetch } = useOnlineUsers()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Wifi className="w-6 h-6 text-green-600 dark:text-green-400" />
              {isConnected && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Online Users
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {onlineCount} user{onlineCount !== 1 ? 's' : ''} currently online
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Connection Status Banner */}
        {!isConnected && (
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm">
            <WifiOff className="w-4 h-4" />
            <span>Reconnecting to live updates...</span>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Globe className="w-12 h-12 mb-3 opacity-50" />
              <p>No users currently online</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <OnlineUserCard key={user.id} user={user} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>Real-time updates {isConnected ? 'active' : 'paused'}</span>
            <button
              onClick={refetch}
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const OnlineUserCard = ({ user }) => {
  const geoInfo = getGeoDisplayInfo(user.geo)

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
      {/* User Info */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
        </div>

        {/* Name & Email */}
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {user.name || 'Unknown User'}
            </span>
            {user.isAdmin && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <Shield className="w-3 h-3" />
                Admin
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
        </div>
      </div>

      {/* Location & Device Info */}
      <div className="flex items-center gap-4 flex-shrink-0 ml-4">
        {/* Geo Location */}
        <div className="text-right">
          {geoInfo.hasGeo ? (
            <div className="flex items-center gap-2">
              <span className="text-xl">{geoInfo.flag}</span>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {geoInfo.country}
                </p>
                {(geoInfo.city || geoInfo.region) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {[geoInfo.city, geoInfo.region].filter(Boolean).join(', ')}
                  </p>
                )}
                {geoInfo.ip && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">{geoInfo.ip}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
              <Globe className="w-4 h-4" />
              <span className="text-sm">Unknown</span>
            </div>
          )}
        </div>

        {/* Device Count */}
        {user.deviceCount > 1 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
            <Monitor className="w-3 h-3" />
            <span>{user.deviceCount}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default OnlineUsersModal
